import React, { useMemo } from "react";
import { useTheme } from "next-themes";
import { ArrowRight, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

const HERO_BG =
  "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/df0b2sox_ChatGPT%20Image%2026%20juin%202026%2C%2018_46_45.png";

function Particles({ count = 22 }) {
  const items = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => {
        const size = 1 + Math.random() * 3.2;
        const left = Math.random() * 100;
        const delay = Math.random() * -30;
        const duration = 18 + Math.random() * 26;
        const opacity = 0.25 + Math.random() * 0.55;
        return { i, size, left, delay, duration, opacity };
      }),
    [count]
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {items.map((p) => (
        <span
          key={p.i}
          className="particle bg-white/70 dark:bg-white/80"
          style={{
            left: `${p.left}%`,
            bottom: `-10px`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            opacity: p.opacity,
            filter: "blur(0.5px)",
          }}
        />
      ))}
    </div>
  );
}

function LightFlow({ isDark }) {
  const accent = isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.06)";
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      <div
        className="hero-blob-a absolute -top-1/3 -left-1/4 w-[70vw] h-[70vw] rounded-full"
        style={{ background: `radial-gradient(closest-side, ${accent}, transparent 70%)`, filter: "blur(40px)" }}
      />
      <div
        className="hero-blob-b absolute top-1/4 -right-1/4 w-[60vw] h-[60vw] rounded-full"
        style={{ background: `radial-gradient(closest-side, ${accent}, transparent 70%)`, filter: "blur(50px)" }}
      />
      <div
        className="hero-blob-c absolute -bottom-1/3 left-1/4 w-[55vw] h-[55vw] rounded-full"
        style={{ background: `radial-gradient(closest-side, ${accent}, transparent 70%)`, filter: "blur(60px)" }}
      />
    </div>
  );
}

export default function Hero() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const scrollTo = (id) => () => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-end justify-center overflow-hidden pt-28 pb-16 md:pb-20"
    >
      {/* Background artwork */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Brams Graphic Design — direction artistique"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "center 20%" }}
          loading="eager"
          data-testid="hero-bg-image"
        />
        {/* Subtle desaturation / scrim layers for theme cohesion */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: isDark
              ? "linear-gradient(180deg, rgba(0,0,0,0.30) 0%, rgba(0,0,0,0.10) 35%, rgba(0,0,0,0.55) 75%, rgba(0,0,0,0.95) 100%)"
              : "linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.25) 50%, rgba(255,255,255,0.85) 100%)",
          }}
        />
        {!isDark && (
          <div className="absolute inset-0 mix-blend-overlay bg-white/15 pointer-events-none" />
        )}
      </div>

      {/* Animated layers ON TOP of image */}
      <LightFlow isDark={isDark} />
      <Particles count={28} />

      {/* Foreground content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pb-6">
        <div
          className={cn(
            "inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[11px] uppercase tracking-[0.3em] mb-8 backdrop-blur-md",
            isDark
              ? "bg-white/10 border border-white/15 text-white"
              : "bg-black/10 border border-black/15 text-black"
          )}
          data-testid="hero-pill"
        >
          <Sparkles size={12} />
          <span>Brams Graphic Design — Studio Indépendant</span>
        </div>

        <h1
          data-testid="hero-headline"
          className={cn(
            "font-display font-bold text-3xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02] max-w-3xl mx-auto",
            isDark ? "text-white" : "text-black"
          )}
          style={{ textShadow: isDark ? "0 2px 30px rgba(0,0,0,0.65)" : "0 2px 20px rgba(255,255,255,0.5)" }}
        >
          L&apos;art visuel <em className="not-italic opacity-70">au service</em> des idées.
        </h1>

        <p
          data-testid="hero-subline"
          className={cn(
            "mt-6 text-sm md:text-lg max-w-2xl mx-auto font-body font-light leading-relaxed",
            isDark ? "text-white/85" : "text-black/75"
          )}
        >
          Créer · Concevoir · Marquer · Inspirer — des identités visuelles et logos pour les
          marques qui veulent durer.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            data-testid="hero-cta-portfolio"
            onClick={scrollTo("portfolio")}
            className={cn(
              "group inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-medium text-sm transition-all duration-300 ease-in-out hover:scale-[1.02] active:scale-95",
              isDark
                ? "bg-white text-black hover:bg-white/90 shadow-[0_0_40px_rgba(255,255,255,0.25)]"
                : "bg-black text-white hover:bg-black/90 shadow-[0_8px_30px_rgba(0,0,0,0.25)]"
            )}
          >
            Voir le Portfolio
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-cta-quote"
            onClick={scrollTo("contact")}
            className={cn(
              "inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-medium text-sm border backdrop-blur-md transition-all duration-300 ease-in-out hover:scale-[1.02] active:scale-95",
              isDark
                ? "border-white/30 bg-white/5 text-white hover:bg-white/10"
                : "border-black/30 bg-black/5 text-black hover:bg-black/10"
            )}
          >
            Demander un Devis
          </button>
        </div>
      </div>
    </section>
  );
}
